/**
 * A community: a roof over related groups.
 *
 * @typedef {object} Community
 * @property {string}      id
 * @property {string}      name
 * @property {string}      description
 * @property {string|null} avatarUrl
 * @property {string|null} announcementChatId
 * @property {boolean}     isAdmin        The caller's own role
 * @property {boolean}     isMember
 * @property {number}      memberCount
 * @property {number}      groupCount     Including the announcement group
 * @property {import('./chat.js').Chat|null} announcement
 * @property {import('./chat.js').Chat[]}    groups   May be a partial list
 * @property {string}      createdBy
 * @property {Date}        createdAt
 */

import { toDate } from '../utils/time.js'
import { toChat } from './chat.js'

/**
 * @param {string} id
 * @param {object} raw
 * @returns {Community}
 */
export function toCommunity (id, raw = {}) {
  const groups = (raw.groups ?? []).map(g => toChat(g.id, g))
  const announcementId = raw.announcementChatId ?? raw.announcement_chat_id ?? null

  // The announcement group arrives either on its own key or inside `groups`,
  // depending on which endpoint produced the row. Resolving it here means no
  // screen has to know which.
  const announcement = raw.announcement
    ? toChat(raw.announcement.id, raw.announcement)
    : groups.find(g => g.id === announcementId) ?? null

  return {
    id,
    name:        raw.name ?? '',
    description: raw.description ?? '',
    avatarUrl:   raw.avatarUrl ?? raw.avatar_url ?? null,
    announcementChatId: announcementId,

    isAdmin:     Boolean(raw.isAdmin ?? raw.is_admin),
    isMember:    Boolean(raw.isMember ?? raw.is_member),
    memberCount: Number(raw.memberCount ?? raw.member_count ?? 0),
    groupCount:  Number(raw.groupCount ?? raw.group_count ?? 0),

    announcement,
    // Without the announcement group: it has its own slot above, and the
    // list in the UI shows it first and separately.
    groups: groups.filter(g => g.id !== announcementId),

    createdBy: raw.createdBy ?? raw.created_by ?? '',
    createdAt: toDate(raw.createdAt ?? raw.created_at) ?? new Date()
  }
}

/**
 * "1,240 members" — the line under a community's name.
 *
 * Grouped with separators rather than abbreviated to 1.2K. A channel's
 * follower count is an audience size, where a round number is honest
 * enough; a community's member count is a list you can open and scroll, so
 * the exact number is the one that matches what you will find there.
 */
export function memberLabel (count = 0) {
  const n = Number(count) || 0
  if (n === 1) return '1 member'
  return `${n.toLocaleString('en-US')} members`
}

/**
 * How many of a community's groups to show in the list before "View all".
 *
 * From docs/references/19-communities.png, which shows the announcement
 * group plus a couple of others — enough to recognise the community, not
 * enough to bury the next one.
 */
export const GROUPS_IN_PREVIEW = 3
